@if (isLayerVisible$ | async) {
  <div class="gtd-panel-wrapper" [class.collapsed]="!(isPanelOpen$ | async)">
    <div class="gtd-panel-card" [class.collapsed]="!(isPanelOpen$ | async)">
      <!-- Panel Header (Always visible & toggleable) -->
      <div class="panel-header" (click)="togglePanel()">
        <div class="header-left">
          <span class="pulse-indicator-red"></span>
          <span class="header-title">Terrorism Database</span>
          @if (stats$ | async; as stats) {
            <span class="pill-badge" title="Filtered Incidents">{{ stats.filteredIncidents | number }}</span>
          }
        </div>

        <div class="header-actions" (click)="$event.stopPropagation()">
          <button
            class="hdr-btn"
            (click)="toggle3dCylinders($event)"
            [class.active]="is3dCylinders$ | async"
            [title]="(is3dCylinders$ | async) ? 'Disable 3D Pillars' : 'Enable 3D Mass Casualty Pillars'"
          >
            <i class="fa-solid fa-cubes-stacked"></i>
          </button>
          <button
            class="hdr-btn"
            (click)="toggleLayer($event)"
            [title]="(isLayerVisible$ | async) ? 'Hide TD Layer' : 'Show TD Layer'"
          >
            <i class="fa-solid" [class.fa-eye]="isLayerVisible$ | async" [class.fa-eye-slash]="!(isLayerVisible$ | async)"></i>
          </button>
          <button
            class="hdr-btn collapse-btn"
            (click)="togglePanel()"
            [title]="(isPanelOpen$ | async) ? 'Collapse Panel' : 'Expand Panel'"
          >
            <i class="fa-solid" [class.fa-chevron-down]="isPanelOpen$ | async" [class.fa-chevron-up]="!(isPanelOpen$ | async)"></i>
          </button>
        </div>
      </div>

      <!-- Expanded Content Body -->
      @if (isPanelOpen$ | async) {
        <div class="panel-body">
          <!-- Loading Overlay -->
          @if (isLoading$ | async) {
            <div class="panel-loading">
              <i class="fa-solid fa-circle-notch fa-spin"></i>
              <span>Loading & Indexing 6,000 Incidents...</span>
            </div>
          }

          <!-- KPI Summary Cards -->
          @if (stats$ | async; as stats) {
            <div class="kpi-grid">
              <div class="kpi-card inc-card">
                <div class="kpi-top">
                  <span class="kpi-label">INCIDENTS</span>
                  <i class="fa-solid fa-crosshairs"></i>
                </div>
                <div class="kpi-val">{{ stats.filteredIncidents | number }}</div>
                <div class="kpi-sub">of {{ stats.totalIncidents | number }} total</div>
              </div>

              <div class="kpi-card kill-card">
                <div class="kpi-top">
                  <span class="kpi-label">FATALITIES</span>
                  <i class="fa-solid fa-skull"></i>
                </div>
                <div class="kpi-val text-red">{{ stats.filteredKilled | number }}</div>
                <div class="kpi-sub">Killed</div>
              </div>

              <div class="kpi-card wound-card">
                <div class="kpi-top">
                  <span class="kpi-label">WOUNDED</span>
                  <i class="fa-solid fa-user-injured"></i>
                </div>
                <div class="kpi-val text-orange">{{ stats.filteredWounded | number }}</div>
                <div class="kpi-sub">Injured</div>
              </div>

              <div class="kpi-card host-card">
                <div class="kpi-top">
                  <span class="kpi-label">HOSTAGES</span>
                  <i class="fa-solid fa-handcuffs"></i>
                </div>
                <div class="kpi-val text-purple">{{ stats.filteredHostages | number }}</div>
                <div class="kpi-sub">Kidnapped</div>
              </div>
            </div>
          }

          <!-- Search Bar -->
          <div class="search-box">
            <i class="fa-solid fa-magnifying-glass search-icon"></i>
            <input
              type="text"
              class="search-input"
              placeholder="Search city, group, weapon, target..."
              [ngModel]="gtdService.filterState.searchQuery"
              (ngModelChange)="onSearchChange($event)"
            />
            @if (gtdService.filterState.searchQuery) {
              <button class="clear-search-btn" (click)="clearSearch()">
                <i class="fa-solid fa-xmark"></i>
              </button>
            }
          </div>

          <!-- Navigation Tabs -->
          <div class="nav-tabs">
            <button
              class="tab-btn"
              [class.active]="activeTab === 'filters'"
              (click)="activeTab = 'filters'"
            >
              <i class="fa-solid fa-filter"></i> Filters
            </button>
            <button
              class="tab-btn"
              [class.active]="activeTab === 'hotspots'"
              (click)="activeTab = 'hotspots'"
            >
              <i class="fa-solid fa-fire"></i> Hotspots
            </button>
            <button
              class="tab-btn"
              [class.active]="activeTab === 'timeline'"
              (click)="activeTab = 'timeline'"
            >
              <i class="fa-solid fa-clock-rotate-left"></i> Timeline
            </button>
          </div>

          <!-- Tab 1: Filters -->
          @if (activeTab === 'filters') {
            <div class="tab-content custom-scroll">
              <!-- Severity Quick Toggles -->
              <div class="section-block">
                <span class="block-title">QUICK TOGGLES</span>
                <div class="toggle-chips-row">
                  <button
                    class="chip-toggle"
                    [class.active]="gtdService.filterState.onlyFatal"
                    (click)="gtdService.toggleOnlyFatal()"
                  >
                    <i class="fa-solid fa-skull"></i> Fatal Only
                  </button>
                  <button
                    class="chip-toggle"
                    [class.active]="gtdService.filterState.onlyKidnappings"
                    (click)="gtdService.toggleOnlyKidnappings()"
                  >
                    <i class="fa-solid fa-person-military-pointing"></i> Kidnappings
                  </button>
                </div>
              </div>

              <!-- Threat Perpetrator Group Filter -->
              <div class="section-block">
                <span class="block-title">PERPETRATOR / THREAT GROUP</span>
                <div class="select-wrap">
                  <select
                    class="gtd-select"
                    [ngModel]="gtdService.filterState.selectedGroup"
                    (ngModelChange)="onGroupChange($event)"
                  >
                    <option value="ALL">All Threat Groups (All Actors)</option>
                    <option value="Boko Haram">Boko Haram (2,900+)</option>
                    <option value="Fulani extremists">Fulani extremists (1,250+)</option>
                    <option value="Movement for the Emancipation of the Niger Delta (MEND)">MEND (Niger Delta)</option>
                    <option value="Indigenous People of Biafra (IPOB)">IPOB</option>
                    <option value="Niger Delta Avengers (NDA)">Niger Delta Avengers</option>
                    <option value="Ansaru (Jama'atu Ansarul Muslimina Fi Biladis Sudan)">Ansaru</option>
                    <option value="Unknown">Unknown / Unclaimed</option>
                  </select>
                </div>
              </div>

              <!-- Attack Types Multiselect Chips -->
              <div class="section-block">
                <div class="block-header-flex">
                  <span class="block-title">ATTACK TYPES</span>
                  <span class="block-sub">Click to toggle</span>
                </div>
                @if (attackTypes$ | async; as types) {
                  <div class="attack-types-wrap">
                    @for (type of types; track type.name) {
                      <button
                        class="attack-chip"
                        [class.active]="isAttackTypeSelected(type.name)"
                        (click)="toggleAttackType(type.name)"
                        [style.--chip-accent]="type.color"
                      >
                        <img [src]="type.iconUrl" [alt]="type.name" class="chip-tactical-icon" />
                        <span class="chip-name">{{ type.name }}</span>
                        <span class="chip-count">{{ type.count }}</span>
                      </button>
                    }
                  </div>
                }
              </div>
            </div>
          }

          <!-- Tab 2: Hotspots -->
          @if (activeTab === 'hotspots') {
            <div class="tab-content custom-scroll">
              <div class="section-block">
                <div class="block-header-flex">
                  <span class="block-title">TOP STATE HOTSPOTS</span>
                  <span class="block-sub">Click to fly</span>
                </div>
                @if (stateHotspots$ | async; as hotspots) {
                  <div class="hotspots-list">
                    @for (item of hotspots | slice:0:12; track item.state) {
                      <div
                        class="hotspot-item"
                        [class.selected]="gtdService.filterState.selectedState === item.state"
                        (click)="onStateClick(item.state, $event)"
                      >
                        <div class="hotspot-left">
                          <i class="fa-solid fa-location-dot hotspot-pin"></i>
                          <div>
                            <div class="hotspot-state-name">{{ item.state }}</div>
                            <div class="hotspot-group-sub">Primary: {{ item.topGroup }}</div>
                          </div>
                        </div>

                        <div class="hotspot-right">
                          <span class="badge-incidents">{{ item.count }}</span>
                          <span class="badge-fatalities" title="Total killed">
                            <i class="fa-solid fa-skull"></i> {{ item.totalKilled }}
                          </span>
                        </div>
                      </div>
                    }
                  </div>
                }
              </div>
            </div>
          }

          <!-- Tab 3: Timeline -->
          @if (activeTab === 'timeline') {
            <div class="tab-content custom-scroll">
              <div class="section-block">
                <span class="block-title">QUICK DECADE PRESETS</span>
                <div class="preset-buttons-row">
                  <button class="preset-btn" [class.active]="selectedDecade === 'ALL'" (click)="onDecadeSelect('ALL')">All (1976-2021)</button>
                  <button class="preset-btn" [class.active]="selectedDecade === '2015+'" (click)="onDecadeSelect('2015+')">2015 - 2021</button>
                  <button class="preset-btn" [class.active]="selectedDecade === '2010-2014'" (click)="onDecadeSelect('2010-2014')">2010 - 2014</button>
                  <button class="preset-btn" [class.active]="selectedDecade === '2000-2009'" (click)="onDecadeSelect('2000-2009')">2000 - 2009</button>
                  <button class="preset-btn" [class.active]="selectedDecade === 'PRE-2000'" (click)="onDecadeSelect('PRE-2000')">1976 - 1999</button>
                </div>
              </div>

              <div class="section-block">
                <span class="block-title">CUSTOM YEAR RANGE</span>
                <div class="year-range-inputs">
                  <div class="input-group-year">
                    <label>From:</label>
                    <input
                      type="number"
                      min="1976"
                      max="2021"
                      [(ngModel)]="gtdService.filterState.minYear"
                      (change)="onYearChange()"
                    />
                  </div>
                  <span class="range-divider">—</span>
                  <div class="input-group-year">
                    <label>To:</label>
                    <input
                      type="number"
                      min="1976"
                      max="2021"
                      [(ngModel)]="gtdService.filterState.maxYear"
                      (change)="onYearChange()"
                    />
                  </div>
                </div>
              </div>
            </div>
          }

          <!-- Panel Footer Controls -->
          <div class="panel-footer">
            <button class="footer-btn reset-filters" (click)="onResetFilters($event)" title="Reset all active filters">
              <i class="fa-solid fa-rotate-left"></i> Reset Filters
            </button>
            <button class="footer-btn overview-btn" (click)="onResetOverview($event)" title="Fly camera to Nigeria overview">
              <i class="fa-solid fa-earth-africa"></i> Country View
            </button>
          </div>
        </div>
      }
    </div>
  </div>
}
